import { intersection } from 'lodash';

import useAuth from '@/hooks/useAuth';
import usePlanOperators from '@/hooks/usePlanOperators';
import { useUserLocations } from '@/hooks/useUserLocations';

import Constants from 'constants/index';

import { Plan } from '@/_generated/api-clients/xp';
import { env } from '@/env';

interface PlanLocationParams {
  /**
   * When true, returns all user's trainer+ locations.
   * Compliance plans don't require ownership intersection.
   */
  isCompliance: boolean;
  /**
   * When true, returns all user's trainer+ locations (same as isCompliance).
   * Readonly plans (e.g., subscribed templates, anchor plans) don't have a
   * specific owner that the user needs to intersect with.
   */
  isReadonly: boolean;
  planOwnerId: string | null;
  planOwnerType: string | null;
}

/** Debug helper for empty location results */
const debugEmptyLocations = (
  context: string,
  details: Record<string, unknown>,
) => {
  if (env.REACT_APP_ENV !== 'prod') {
    console.debug(`[usePlanLocations] Empty result for ${context}:`, details);
  }
};

/**
 * Computes locations for a given plan based on plan ownership.
 * - Compliance/readonly plans: all locations where user has trainer+ permission
 * - Non-compliance plans: intersection of plan owner's locations and user's trainer+ locations
 * - Support center plans: falls back to '00000' when no assignable locations exist
 * - Licensee users: restricted to only their selected location
 * - Excludes '00000' (support staff location) from all results
 *
 * Falls back to user's locations if intersection is empty or data unavailable.
 *
 * @remarks
 * **Why '00000' is excluded:**
 * The '00000' location represents the Support Center—a virtual location for internal
 * staff users. It must be filtered out because:
 *
 * 1. **No real team members exist there** — It's not a physical store, so querying
 *    team members for '00000' returns incorrect results.
 * 2. **Readonly plans (ANCHOR, subscribed templates) originate from Support Center** —
 *    Without filtering, these plans would incorrectly pass '00000' as a location parameter.
 * 3. **Staff user data is tied to ADID, not location** — Internal users are associated
 *    via their Active Directory ID rather than the '00000' location.
 * 4. **Current user filtering should be scoped** — Support center users should only
 *    see their own data, not all users associated with '00000'.
 * @deprecated use GetLocationsByPlan.
 */
type GetLocationsByPlan_LEGACY = (plan: PlanLocationParams | Plan) => string[];

type PlanLocationFields = Pick<Plan, 'type'> &
  Partial<Pick<Plan, 'readonly' | 'ownerId' | 'ownerType'>>;

type GetLocationsByPlan = (plan: PlanLocationFields) => string[];

/**
 * Hook that returns a function to compute locations for a specific plan.
 *
 * The returned function determines which locations a user can assign team members from
 * based on the plan's ownership and the user's permissions.
 *
 * @example
 * ```tsx
 * const getLocationsByPlan = usePlanLocations();
 *
 * const locations = getLocationsByPlan({
 *   isCompliance: plan.type === 'COMPLIANCE',
 *   isReadonly: plan.readonly ?? false,
 *   planOwnerId: plan.ownerId ?? null,
 *   planOwnerType: plan.ownerType ?? null,
 * });
 * ```
 * @deprecated use usePlanLocations.
 */
export const usePlanLocations_LEGACY = (): GetLocationsByPlan_LEGACY => {
  const { data: auth } = useAuth();
  const userLocations = useUserLocations();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const allLocationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const { operators, isFetching } = usePlanOperators();

  return (planInput: PlanLocationParams | Plan): string[] => {
    // Return user's locations while operators are loading
    if (isFetching) return userLocations;

    const isCompliance =
      'type' in planInput
        ? planInput.type === 'COMPLIANCE'
        : planInput.isCompliance;
    const isReadonly =
      'readonly' in planInput ? planInput.readonly : planInput.isReadonly;
    const planOwnerId =
      'ownerId' in planInput ? planInput.ownerId : planInput.planOwnerId;
    const planOwnerType =
      'ownerType' in planInput ? planInput.ownerType : planInput.planOwnerType;

    // Licensee users: restricted to only their selected location
    if (isLicenseeUser) {
      if (userLocations.length === 0) {
        debugEmptyLocations('licensee user', {
          reason: 'No active licensee location selected in picker',
          userId: auth?.userId,
        });
      }
      return userLocations;
    }

    let result: string[];
    let debugDetails: Record<string, unknown>;

    // Compliance, readonly, and support-center plans: user can select from all
    // their trainer+ locations. These plan types don't require ownership
    // intersection.
    //
    // SUPPORT_CENTER must be handled here (before the operator lookup) because
    // the support-center entity is never in the user's assignableOperators —
    // usePlanOperators only returns operators where the user holds leader/TG
    // permissions. Letting a SUPPORT_CENTER plan fall into the else branch
    // always yields ownerLocations=[], assignableLocations=[], and triggers
    // the ['00000'] hardcoded fallback.
    if (
      isCompliance ||
      isReadonly ||
      planOwnerType === Constants.PLAN_OWNER_TYPE.SUPPORT_CENTER
    ) {
      const assignableLocations = allLocationsWithAtLeastTrainer.filter(
        loc => loc !== Constants.SUPPORT_STAFF_LOCATION,
      );
      // Only SUPPORT_CENTER plans fall back to '00000'; compliance/readonly
      // plans have no meaningful fallback if the user has no trainer+ locations.
      result =
        assignableLocations.length > 0 ||
        planOwnerType !== Constants.PLAN_OWNER_TYPE.SUPPORT_CENTER
          ? assignableLocations
          : [Constants.SUPPORT_STAFF_LOCATION];
      debugDetails = {
        context: 'compliance/readonly/support-center plan',
        userId: auth?.userId,
        isCompliance,
        isReadonly,
        planOwnerType,
      };
    } else {
      // Non-compliance: find the plan owner's locations that user has access to
      const ownerLocations =
        operators.find(op => op.id === planOwnerId)?.locations ?? [];

      const roleBasedLocations =
        auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.TRAINER
          ? allLocationsWithAtLeastTrainer
          : intersection(ownerLocations, allLocationsWithAtLeastTrainer);

      result = roleBasedLocations.filter(
        loc => loc !== Constants.SUPPORT_STAFF_LOCATION,
      );
      debugDetails = {
        context: 'non-compliance plan',
        planOwnerId,
        planOwnerType,
        ownerLocations,
        userTrainerLocations: allLocationsWithAtLeastTrainer,
      };
    }

    // Fallback to user's locations if no results found
    // NOTE: This fallback is a QA requirement to ensure users can always
    // interact with plans even when location intersection logic fails or
    // returns empty results
    if (result.length === 0) {
      debugEmptyLocations('Falling back to userLocations', {
        ...debugDetails,
        userLocations,
      });
      return userLocations;
    }
    return result;
  };
};

export const usePlanLocations = (): GetLocationsByPlan => {
  const { data: auth } = useAuth();
  const userLocations = useUserLocations();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const allLocationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const { operators, isFetching } = usePlanOperators();

  return (plan: PlanLocationFields): string[] => {
    if (isFetching) return userLocations;

    if (isLicenseeUser) {
      if (userLocations.length === 0) {
        debugEmptyLocations('licensee user', {
          reason: 'No active licensee location selected in picker',
          userId: auth?.userId,
        });
      }
      return userLocations;
    }

    const isCompliance = plan.type === 'COMPLIANCE';
    const isReadonly = plan.readonly;

    let result: string[];
    let debugDetails: Record<string, unknown>;

    if (isCompliance || isReadonly) {
      result = allLocationsWithAtLeastTrainer.filter(
        loc => loc !== Constants.SUPPORT_STAFF_LOCATION,
      );
      debugDetails = {
        context: 'compliance/readonly plan',
        userId: auth?.userId,
        isCompliance,
        isReadonly,
      };
    } else {
      const ownerLocations =
        operators.find(op => op.id === plan.ownerId)?.locations ?? [];

      const roleBasedLocations =
        auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.TRAINER
          ? allLocationsWithAtLeastTrainer
          : intersection(ownerLocations, allLocationsWithAtLeastTrainer);

      const assignableLocations = roleBasedLocations.filter(
        loc => loc !== Constants.SUPPORT_STAFF_LOCATION,
      );

      if (plan.ownerType === Constants.PLAN_OWNER_TYPE.SUPPORT_CENTER) {
        result =
          assignableLocations.length > 0
            ? assignableLocations
            : [Constants.SUPPORT_STAFF_LOCATION];
        debugDetails = {
          context: 'SUPPORT_CENTER plan',
          planOwnerId: plan.ownerId,
          assignableLocations,
          ownerLocations,
          userTrainerLocations: allLocationsWithAtLeastTrainer,
        };
      } else {
        result = assignableLocations;
        debugDetails = {
          context: 'non-compliance plan',
          planOwnerId: plan.ownerId,
          planOwnerType: plan.ownerType,
          ownerLocations,
          userTrainerLocations: allLocationsWithAtLeastTrainer,
        };
      }
    }

    if (result.length === 0) {
      debugEmptyLocations('Falling back to userLocations', {
        ...debugDetails,
        userLocations,
      });
      return userLocations;
    }

    return result;
  };
};

export default usePlanLocations_LEGACY;
